import { UNDERLINE_STATIC } from '@/components/ui/link-underline';
import { lazy, Suspense, useState } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { ChevronDownIcon } from '@components/icons/ChevronDownIcon';
import { EmptySkuIcon } from '@components/icons/EmptySkuIcon';
import { PdfFileIcon } from '@components/icons/PdfFileIcon';
import { LoadingSpinnerIcon } from '@components/icons/LoadingSpinnerIcon';
import { cn } from '@/lib/utils';
import { FileAttachment, FileIconBox } from '@/components/ui/file-attachment';
import { useTranslation } from '../../hooks/useTranslation';
import { JobStatusIcon } from './JobStatusIcon';
import { formatJobTimestamp } from './formatJobTimestamp';
import { isEventOrderItem, subjectEditUrl } from './subjectEditUrl';
import type { ApiAsyncTaskItem } from '@api/asyncTasks';

// react-markdown + its remark/mdast/unified pipeline is ~30KB+ gzipped and is
// only needed once a user expands an item with result_details. Lazy load so
// the Activity Centre's initial bundle stays slim.
const MarkdownContent = lazy(() =>
  import('./MarkdownContent').then((mod) => ({ default: mod.MarkdownContent })),
);

interface JobCardItemProps {
  item: ApiAsyncTaskItem;
}

export function JobCardItem({ item }: JobCardItemProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const fixUrl = item.state === 'failed' ? subjectEditUrl(item) : null;
  const showTryAgain = item.state === 'failed' && isEventOrderItem(item);
  // Downloadable only while the artifact is fresh — artifact_url is nulled once
  // past retention, so an expired item simply shows no link.
  const downloadUrl = item.state === 'succeeded' ? item.artifact_url : null;
  const isProcessing = item.state === 'pending' || item.state === 'in_progress';
  const hasDetails = !isProcessing && Boolean(item.result_details?.trim());
  const detailsId = `job-item-${item.id}-details`;
  const failureReason = item.state === 'failed' ? errorMessage(item) : null;

  // Whole row is the click target when there are details to reveal. Using a
  // div with role=button (not <button>) so the Fix/Download <a> inside the
  // row stays a valid interactive — nesting <a> in <button> is invalid HTML.
  const toggle = () => setOpen((prev) => !prev);
  const onRowKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggle();
    }
  };
  const stopRowToggle = (event: MouseEvent) => {
    event.stopPropagation();
  };

  const rowInteractiveProps = hasDetails
    ? {
        role: 'button' as const,
        tabIndex: 0,
        'aria-expanded': open,
        // Only point at detailsId while the panel is actually mounted —
        // MarkdownContent is React.lazy + Suspense, rendered conditionally
        // on `open`, so a permanent aria-controls would dangle when closed.
        'aria-controls': open ? detailsId : undefined,
        'aria-label': t(
          open ? 'job_card.item.hide_details_aria' : 'job_card.item.details_aria',
          { title: item.title },
        ),
        onClick: toggle,
        onKeyDown: onRowKeyDown,
      }
    : {};

  return (
    <li className="flex flex-col w-full font-inter">
      <div
        {...rowInteractiveProps}
        className={cn(
          'flex items-center gap-8 w-full p-2 rounded-input',
          hasDetails &&
            'cursor-pointer hover:bg-dinnerplate-white-lighter focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-off-black',
        )}
      >
        <FileAttachment
          className="flex-1 gap-4"
          leading={<Thumbnail item={item} />}
          name={item.title}
          nameAdornment={
            hasDetails ? (
              <ChevronDownIcon
                aria-hidden="true"
                className={cn(
                  'shrink-0 size-3 text-off-black chevron-motion',
                  open && 'rotate-180',
                )}
              />
            ) : undefined
          }
          subtitle={!isProcessing ? <ItemSubtitle item={item} /> : undefined}
        />
        <div className="flex items-center gap-4 shrink-0">
          {showTryAgain ? (
            <span className="text-sm font-semibold text-grey">
              {t('job_card.item.try_again')}
            </span>
          ) : (
            fixUrl && (
              <a
                href={fixUrl}
                onClick={stopRowToggle}
                className={cn('text-sm font-semibold text-off-black rounded-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-off-black', UNDERLINE_STATIC)}
                aria-label={t('job_card.item.fix_aria', { title: item.title })}
              >
                {t('job_card.item.fix')}
              </a>
            )
          )}
          {downloadUrl && (
            <a
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={stopRowToggle}
              className={cn('text-sm font-semibold text-off-black rounded-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-off-black', UNDERLINE_STATIC)}
              aria-label={t('job_card.item.download_aria', { title: item.title })}
            >
              {t('job_card.item.download')}
            </a>
          )}
          {isProcessing ? (
            <ProcessingBadge label={t('job_card.item.processing')} />
          ) : (
            <JobStatusIcon state={item.state} />
          )}
        </div>
      </div>
      {failureReason && (
        <p className="px-2 pb-2 text-xs leading-tight-sm text-chilli-red">{failureReason}</p>
      )}
      {hasDetails && open && (
        <div id={detailsId} className="pt-2">
          <Suspense fallback={null}>
            <MarkdownContent value={item.result_details} />
          </Suspense>
        </div>
      )}
    </li>
  );
}

// The worker's actionable reason ("… isn't linked to Sling"); system errors
// already carry a generic message.
function errorMessage(item: ApiAsyncTaskItem): string | null {
  const message = item.error?.message;
  return typeof message === 'string' && message.trim() ? message : null;
}

function Thumbnail({ item }: { item: ApiAsyncTaskItem }) {
  // Artifact items show a format icon (PDF for now); unknown formats fall
  // through to no icon, same as before.
  const formatIcon = artifactFormatIcon(item);
  if (formatIcon) {
    return <FileIconBox className="size-16">{formatIcon}</FileIconBox>;
  }
  if (item.image_url) {
    return (
      <img
        src={item.image_url}
        alt=""
        width={48}
        height={48}
        loading="lazy"
        className="size-16 rounded-xs object-cover bg-dinnerplate-white shrink-0"
      />
    );
  }
  // Products without an image fall back to the SKU placeholder so the row
  // still has a visual anchor. Other subjects (e.g. date-based time-band
  // items) intentionally render nothing — the title is enough.
  if (item.subject_type === 'Product') {
    return (
      <FileIconBox className="size-16">
        <EmptySkuIcon className="size-8 text-grey" />
      </FileIconBox>
    );
  }
  return null;
}

// Only PDF for now; other formats render no icon.
function artifactFormatIcon(item: ApiAsyncTaskItem) {
  if (!item.expects_artifact) return null;

  const extension = item.title.split('.').pop()?.toLowerCase();
  if (extension === 'pdf') return <PdfFileIcon className="size-8 text-grey" data-testid="pdf-file-icon" />;

  return null;
}

function ItemSubtitle({ item }: { item: ApiAsyncTaskItem }) {
  const { t } = useTranslation();

  if (item.state === 'failed') {
    const iso = item.ended_at ?? item.started_at;
    const date = iso ? formatJobTimestamp(iso) : '';
    return (
      <span className="text-xs font-normal leading-tight-sm text-grey truncate">
        {t('job_card.item.completed_at', { date })}
        <span aria-hidden="true">{' · '}</span>
        <span className="text-chilli-red">
          {t('job_card.item.failed_count', { count: 1 })}
        </span>
      </span>
    );
  }

  if (item.state === 'succeeded') {
    const iso = item.ended_at ?? item.started_at;
    const date = iso ? formatJobTimestamp(iso) : '';
    return (
      <span className="text-xs font-normal leading-tight-sm text-grey truncate">
        {t('job_card.item.completed_at', { date })}
      </span>
    );
  }

  return null;
}

function ProcessingBadge({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-1 shrink-0">
      <LoadingSpinnerIcon className="size-3 text-grey" />
      <span className="text-xs font-semibold leading-tight-sm text-grey">{label}</span>
    </div>
  );
}
